import React from 'react';
import { bool, func, object, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { NamedLink } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarSearchForm from '../TopbarContainer/Topbar/TopbarSearchForm/TopbarSearchForm';

import css from './CategoriesPage.module.css';

export const CategoriesPageComponent = props => {
  const { scrollingDisabled, pageAssetsData, inProgress, intl, history } = props;

  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();

    // console.log('Rendering CategoriesPage', JSON.stringify({ pageAssetsData, inProgress }));

  const title = intl.formatMessage({ id: 'CategoriesPage.title' });
  const description = intl.formatMessage({ id: 'CategoriesPage.description' });

  // Handle search form submission
  const handleSearchSubmit = values => {
    if (!history) {
      console.error('History is not available');
      return;
    }

    const { keywords, location } = values;
    const searchParams = keywords ? { keywords } : location ? { address: location.search, bounds: location.selectedPlace?.bounds } : {};

    const path = createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams);
    history.push(path);
  };

  // Get categories data from CMS
  const categoriesAsset = pageAssetsData?.categories;
  const categoriesData = categoriesAsset?.data;

  // Handle both possible data structures
  const dataRoot = categoriesData?.data || categoriesData || {};
  const sections = dataRoot.sections || [];
  const included = dataRoot.included || [];

  // Extract meta data
  const meta = dataRoot.meta || {};
  const pageTitle = meta?.pageTitle?.content || title;
  const pageDescription = meta?.pageDescription?.content || description;

  // Extract sections by sectionType and sectionName
  const categoriesSection = sections.find(s => s.sectionName === 'Categories' && s.sectionType === 'columns');
  const popularSearchesSections = sections.filter(s =>
    s.sectionName?.startsWith('Popular Searches') && s.sectionType === 'columns'
  );
  const searchOtherModelsSection = sections.find(s => s.sectionId === 'category-page-search');

  // console.log('Categories section blocks:', categoriesSection?.blocks?.length);
  // console.log('Popular searches sections:', popularSearchesSections.length);
  // console.log('First category block:', categoriesSection?.blocks?.[0]);

  // Helper function to get image URL from block
  const getImageUrl = (block) => {
    if (block?.media?.fieldType !== 'image') {
      // console.log('Not an image field type:', block?.media?.fieldType);
      return '';
    }

    // After denormalization, image data is directly in block.media.image.attributes.variants
    if (block?.media?.image?.attributes?.variants) {
      const variants = block.media.image.attributes.variants;
      const url = variants.landscape1200?.url ||
             variants.landscape800?.url ||
             variants.landscape400?.url ||
             '';
      // console.log('Image URL found:', url);
      return url;
    }

    // console.log('No image variants found for block:', block.blockName);
    return '';
  };

  // Helper function to get link href from block
  const getLinkHref = (block) => {
    if (block?.media?.link?.fieldType === 'internalImageLink') {
      return block.media.link.href || '';
    } else if (block?.media?.link?.fieldType === 'externalImageLink') {
      return block.media.link.href || '';
    }
    return '';
  };

  // Render category cards
  const renderCategoryCards = (blocks) => {
    if (!blocks || blocks.length === 0) return null;

    return blocks.map((block, index) => {
      const imageUrl = getImageUrl(block);
      const linkHref = getLinkHref(block);
      const title = block.title?.content || block.blockName || '';
      const alt = block.media?.alt || title || '';

      if (linkHref) {
        return (
          <NamedLink
            key={index}
            name="SearchPage"
            to={{ search: linkHref.replace('/s', '') }}
            className={css.categoryCard}
          >
            <div className={css.categoryImageWrapper}>
              {imageUrl && (
                <img src={imageUrl} alt={alt} className={css.categoryImage} />
              )}
            </div>
            <div className={css.categoryContent}>
              <h3 className={css.categoryTitle}>{title}</h3>
            </div>
          </NamedLink>
        );
      }

      return (
        <div key={index} className={css.categoryCard}>
          <div className={css.categoryImageWrapper}>
            {imageUrl && (
              <img src={imageUrl} alt={alt} className={css.categoryImage} />
            )}
          </div>
          <div className={css.categoryContent}>
            <h3 className={css.categoryTitle}>{title}</h3>
          </div>
        </div>
      );
    });
  };

  // Render popular search cards
  const renderPopularSearchCards = (blocks) => {
    if (!blocks || blocks.length === 0) return null;

    return blocks.map((block, index) => {
      const imageUrl = getImageUrl(block);
      const linkHref = getLinkHref(block);
      const title = block.title?.content || block.blockName || '';
      const alt = block.media?.alt || title || '';

      if (linkHref) {
        return (
          <NamedLink
            key={index}
            name="SearchPage"
            to={{ search: linkHref.replace('/s', '') }}
            className={css.searchCard}
          >
            <div className={css.searchImageWrapper}>
              {imageUrl && (
                <img src={imageUrl} alt={alt} className={css.searchImage} />
              )}
            </div>
            <div className={css.searchContent}>
              <h3 className={css.searchTitle}>{title}</h3>
            </div>
          </NamedLink>
        );
      }

      return (
        <div key={index} className={css.searchCard}>
          <div className={css.searchImageWrapper}>
            {imageUrl && (
              <img src={imageUrl} alt={alt} className={css.searchImage} />
            )}
          </div>
          <div className={css.searchContent}>
            <h3 className={css.searchTitle}>{title}</h3>
          </div>
        </div>
      );
    });
  };

  // Merge all popular searches blocks
  const allPopularSearchesBlocks = popularSearchesSections.reduce((acc, section) => {
    return [...acc, ...(section.blocks || [])];
  }, []);

  // Show loading state
  if (inProgress) {
    return (
      <Page title={pageTitle} description={pageDescription} scrollingDisabled={scrollingDisabled}>
        <LayoutSingleColumn
          topbar={<TopbarContainer />}
          footer={<FooterContainer />}
          mainColumnClassName={css.layoutWrapperMain}
        >
          <div className={css.loading}>
            <FormattedMessage id="CategoriesPage.loading" />
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  return (
    <Page title={pageTitle} description={pageDescription} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        mainColumnClassName={css.layoutWrapperMain}
      >
        <div className={css.root}>
          {/* Visually hidden H1 for SEO */}
          <h1 className={css.visuallyHidden}>
            Porsche For Sale Categories | rennzone
          </h1>

          {/* Section 1: Categories */}
          {categoriesSection && (
            <section className={css.categoriesSection}>
              <div className={css.categoriesContainer}>
                <div className={css.sectionHeader}>
                  <h2 className={css.sectionTitle}>
                    {categoriesSection.title?.content || (
                      <FormattedMessage id="CategoriesPage.categoriesTitle" />
                    )}
                  </h2>
                  <NamedLink
                    name="SearchPage"
                    className={css.viewAllButton}
                  >
                    <FormattedMessage id="CategoriesPage.viewAll" />
                  </NamedLink>
                </div>
                <div className={css.categoriesGrid}>
                  {renderCategoryCards(categoriesSection.blocks)}
                </div>
              </div>
            </section>
          )}

          {/* Section 2: Popular Searches */}
          {allPopularSearchesBlocks.length > 0 && (
            <section className={css.popularSearchesSection}>
              <div className={css.popularSearchesContainer}>
                <div className={css.popularSearchesHeader}>
                  <h2 className={css.popularSearchesTitle}>
                    {popularSearchesSections[0]?.title?.content || (
                      <FormattedMessage id="CategoriesPage.popularSearchesTitle" />
                    )}
                  </h2>
                  <NamedLink
                    name="SearchPage"
                    to={{ search: '?pub_categoryLevel1=cars-standard-listing&pub_categoryLevel2=porsche-911-for-sale' }}
                    className={css.viewAll911Button}
                  >
                    <FormattedMessage id="CategoriesPage.viewAll911" />
                  </NamedLink>
                </div>
                <div className={css.popularSearchesGrid}>
                  {renderPopularSearchCards(allPopularSearchesBlocks)}
                </div>
              </div>
            </section>
          )}

          {/* Section 3: Search Other Models */}
          {searchOtherModelsSection && (
            <section className={css.searchOtherModelsSection}>
              <div className={css.searchOtherModelsContainer}>
                <h2 className={css.searchOtherModelsTitle}>
                  {searchOtherModelsSection.title?.content || (
                    <FormattedMessage id="CategoriesPage.searchOtherModelsTitle" />
                  )}
                </h2>
                <div className={css.searchFormWrapper}>
                  <TopbarSearchForm
                    className={css.searchForm}
                    desktopInputRoot={css.searchInputRoot}
                    onSubmit={handleSearchSubmit}
                    appConfig={config}
                  />
                </div>
              </div>
            </section>
          )}

          {/* Porsche Guide Section */}
          <section className={css.guideSection}>
            <div className={css.guideContainer}>
              {/* Header */}
              <div className={css.guideBlock}>
                <h1 className={css.guideH1}>
                  The Ultimate Guide to Finding Your Perfect Porsche for Sale
                </h1>
                <p className={css.guideText}>
                  Whether you are a seasoned collector searching for a rare air-cooled classic or a
                  first-time buyer looking to enter the world of Stuttgart's finest, navigating the various
                  Porsche categories can be a journey in itself. At Rennzone, we have curated a
                  marketplace that celebrates the entire lineage of the marque, providing a streamlined
                  experience for enthusiasts to buy and sell{' '}
                  <a href="/s?pub_categoryLevel1=cars-standard-listing">Porsche road cars</a>, race cars, and parts.
                </p>
              </div>

              {/* Understanding the Porsche 911 Generations */}
              <div className={css.guideBlock}>
                <h2 className={css.guideH2}>Understanding the Porsche 911 Generations</h2>
                <p className={css.guideText} style={{ marginBottom: 16 }}>
                  The Porsche 911 remains the definitive high-performance sports car, maintaining its
                  iconic rear-engine layout for over 60 years. To find the right Porsche 911 for sale, it
                  is essential to understand the evolution of its generations:
                </p>
                <ul className={css.guideList}>
                  <li>
                    <strong>Classic Air-Cooled Era (1963–1998):</strong> This period includes the <strong>Original 911
                    (1963–1973)</strong>, the <strong>930 (1974–1989)</strong> with its characteristically angry
                    bumpers, the technologically advanced <strong>964 (1989–1994)</strong>, and the highly
                    refined <strong>993 (1993–1998)</strong>. The 993 is particularly significant as the final air-cooled 911, blending classic character with modern multilink suspension.
                  </li>
                  <li>
                    <strong>The Water-Cooled Revolution (1999–Present):</strong> Beginning with the <strong>996
                    generation</strong>, Porsche transitioned to water-cooled engines to meet
                    performance and emission standards. This era continued through the refined
                    <strong> 997</strong>, the larger and more luxurious <strong>991</strong>, and the current <strong>992 generation</strong>,
                    which features twin-turbocharged engines across nearly the entire range.
                  </li>
                </ul>
              </div>

              {/* Specialized Porsche Categories */}
              <div className={css.guideBlock}>
                <h2 className={css.guideH2}>Specialized Porsche Categories: GT Cars and RS Models</h2>
                <p className={css.guideText} style={{ marginBottom: 16 }}>
                  For those demanding track-ready performance, the GT and RS categories represent
                  the pinnacle of Porsche engineering:
                </p>
                <ul className={css.guideList}>
                  <li>
                    <strong>Porsche GT3 &amp; GT2:</strong> These models are stripped of excess weight and tuned
                    for high-revving exhilaration. Finding a <strong>Porsche GT3 for sale</strong> often means
                    acquiring a vehicle that is part race car, part road machine.
                  </li>
                  <li>
                    <strong>RS (<s>Rennsport</s>) Models:</strong> Historically, the "RS" badge, such as on the
                    legendary <strong>1973 Carrera RS 2.7</strong>, signifies a homologation special designed for
                    the circuit. Modern RS variants continue this tradition, offering the most
                    uncompromising driving experience available in a road-legal car.
                  </li>
                </ul>
              </div>

              {/* Beyond the 911 */}
              <div className={css.guideBlock}>
                <h2 className={css.guideH2}>Beyond the 911: Mid-Engine Brilliance and Practical Performance</h2>
                <p className={css.guideText} style={{ marginBottom: 16 }}>
                  While the 911 is the heart of the brand, other Porsche categories offer unique driving
                  dynamics and everyday versatility:
                </p>
                <ul className={css.guideList}>
                  <li>
                    <strong>718 Boxster &amp; Cayman:</strong> These mid-engine masterpieces provide exceptional
                    balance and agility. From the entry-level 986 and 987 generations to the
                    modern <strong>718 Cayman GT4</strong>, the mid-engine platform is a Porsche for purists.
                  </li>
                  <li>
                    <strong>Porsche SUV Lineup:</strong> The <strong>Cayenne</strong> and <strong>Macan</strong> have redefined the luxury
                    SUV market by proving that practicality and Porsche DNA can coexist. The
                    Macan offers a more compact, agile feel, while the Cayenne provides serious
                    towing capability and high-performance E-Hybrid options.
                  </li>
                </ul>
              </div>

              {/* Panamera & Taycan */}
              <div className={css.guideBlock}>
                <ul className={css.guideList}>
                  <li>
                    <strong>Panamera &amp; Taycan:</strong> The <strong>Panamera</strong> remains the premier "sports car for
                    four," while the all-electric <strong>Taycan</strong> represents the future of the brand, offering
                    supercar-level acceleration with zero emissions.
                  </li>
                </ul>
              </div>

              {/* Buying a Used Porsche */}
              <div className={css.guideBlock}>
                <h2 className={css.guideH2}>Buying a Used Porsche: What to Look For</h2>
                <p className={css.guideText} style={{ marginBottom: 16 }}>
                  When browsing our listings, it is important to perform due diligence to ensure you are
                  making a sound investment:
                </p>
                <ul className={css.guideList}>
                  <li>
                    <strong>Service History &amp; Provenance:</strong> A detailed service record is critical for
                    verifying mileage and ensuring the car has been maintained by Porsche
                    specialists.
                  </li>
                  <li>
                    <strong>Pre-Purchase Inspections (PPI):</strong> We highly recommend a PPI from an
                    independent specialist, which typically covers mechanical health, diagnostic
                    scans, and paint depth analysis to spot hidden accident damage.
                  </li>
                  <li>
                    <strong>Common Issues:</strong> Buyers of 1997–2008 models should be aware of IMS
                    bearing concerns, while air-cooled enthusiasts should inspect for rust and
                    engine wear.
                  </li>
                </ul>
              </div>

              {/* Why Choose Rennzone */}
              <div className={css.guideBlock}>
                <h2 className={css.guideH2}>Why Choose Rennzone for Your Porsche Purchase?</h2>
                <p className={css.guideText}>
                  Rennzone is more than just a listing site; it is a dedicated hub for the Porsche
                  community. By focusing exclusively on one marque, we ensure that buyers are
                  connected with sellers who truly understand these vehicles. Whether you are looking
                  for an investment-grade <strong>964 RS</strong> NOT for a reliable Cayenne for daily duties, our
                  curated listings and tools and reach to help you find your next Porsche with
                  confidence.
                </p>
                <p className={css.guideText} style={{ marginTop: 16 }}>
                  Explore our categories today and join a legacy of performance, engineering
                  excellence, and timeless design.
                </p>
              </div>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

CategoriesPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  pageAssetsData: object,
  inProgress: bool,
  intl: intlShape.isRequired,
  history: shape({
    push: func.isRequired,
  }).isRequired,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress } = state.hostedAssets || {};
  return {
    scrollingDisabled: state.ui?.scrollingDisabled || false,
    pageAssetsData,
    inProgress,
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
const CategoriesPage = compose(
  injectIntl,
  withRouter,
  connect(mapStateToProps)
)(CategoriesPageComponent);

export default CategoriesPage;
